import { useCollision, checkCollider, RECT, CIRCLE } from '@lib/systems/collision'
import { useMouseInput, LEFT_BUTTON, RIGHT_BUTTON } from '@lib/systems/mouseInput'
import { useUpdate } from '@lib/systems/update'
import { useDebugRenderer } from '@lib/renderers/debug'
import debug from '@lib/util/debug'

export const LABEL = 0
export const BUTTON = 1

const FRESH = 1
const STALE = 2

const { position: mousePos, btnPressed } = useMouseInput()

class Ui {
  constructor () {
    this.state = {}
    this.elements = {}
    this.elementsHash = {}
    this.elementId = 0
    this.lastId = 0
    this.drawDebug = false
  }

  init () {
    if (this._ready) return
    this._ready = true

    useCollision({
      group: 'uiCursor',
      shape: CIRCLE,
      position: mousePos,
      radius: 2
    })

    useUpdate(() => {
      this.elementId = 0

      for (const key in this.elementsHash) {
        if (this.elementsHash[key] === FRESH) {
          this.elementsHash[key] = STALE
        }
      }
    }, {
      updateOrder: -999
    })

    useUpdate(() => {
      for (const key in this.elementsHash) {
        if (this.elementsHash[key] === STALE) {
          delete this.elementsHash[key]
          delete this.elements[key]
        }
      }
    }, {
      updateOrder: 999
    })

    useDebugRenderer((ctx) => {
      if (!debug.draw) return

      let el
      for (const key in this.elements) {
        el = this.elements[key]

        if (el.kind === LABEL) {
          ctx.globalAlpha = 0.25
          ctx.strokeStyle = '#FFF1E8'
        } else {
          ctx.strokeStyle = el.state.over ? '#FFF1E8' : '#FFEC27'
        }

        ctx.lineWidth = 2
        ctx.beginPath()
        ctx.rect(el.x - el.width / 2, el.y - el.height / 2, el.width, el.height)
        ctx.stroke()

        ctx.globalAlpha = 1

        if (el.text) {
          ctx.fillStyle = '#FFF1E8'
          ctx.font = '16px monospace'
          ctx.textBaseline = el.baseline || 'middle'
          ctx.textAlign = el.align || 'center'
          ctx.fillText(el.text, el.x, el.y)
        }
      }
    }, {
      drawOrder: 999
    })
  }

  useLabel (props) {
    const id = this.elementId++

    this.elementsHash[id] = FRESH

    this.elements[id] = {
      ...props,
      kind: LABEL
    }
  }

  useButton (props) {
    const id = this.elementId++

    const buttonState = {}

    this.elementsHash[id] = FRESH

    this.elements[id] = {
      ...props,
      kind: BUTTON,
      state: buttonState
    }

    if (checkCollider({
      position: props,
      shape: RECT,
      width: props.width,
      height: props.height
    }, 'uiCursor')) {
      buttonState.over = false
      if (btnPressed(LEFT_BUTTON)) {
        buttonState.pressed = LEFT_BUTTON
      } else if (btnPressed(RIGHT_BUTTON)) {
        buttonState.pressed = RIGHT_BUTTON
      } else {
        buttonState.pressed = false
        buttonState.over = true
      }
    }

    return buttonState
  }
}

const ui = new Ui()

export function useUi () {
  ui.init()

  return {
    state: ui.state,
    button: ui.useButton.bind(ui),
    label: ui.useLabel.bind(ui)
  }
}
